// src/components/Header/Header.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  Menu, 
  X, 
  Home, 
  User, 
  LogOut, 
  BookOpen, 
  TrendingUp, 
  GraduationCap,
  Users,
  FileText,
  Info,
  Mail,
  CreditCard
} from "lucide-react";
import Logo from "../Logo/Logo";
import "./Header.css";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const isAuthenticated = !!(
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token")
  );
  
  const userRole =
    localStorage.getItem("role") || sessionStorage.getItem("role");
  
  const userName =
    localStorage.getItem("name") || sessionStorage.getItem("name") || "User";

  // Close sidebar when route changes
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isSidebarOpen]);

  const handleLogout = () => {
    console.log("🚪 Logging out...");
    
    // ✅ STEP 1: Clear all localStorage keys
    const localStorageKeys = [
      // Authentication tokens
      "access_token",
      "refresh_token",
      "token_type",
      
      // User identification
      "role",
      "username",
      "email",
      "user_id",
      "userId",  // ✅ Added - used by TutorAgent
      "name",
      "surname",
      "student_id",
      "teacher_id",
      
      // Session data
      "currentSessionId",
      "activeConversation",
      
      // Agent-specific data
      "lastAgentSession",
      "agentContext",
      
      // Preferences
      "theme",
      "language",
      "notifications"
    ];

    localStorageKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`  🗑️ Removing localStorage: ${key}`);
        localStorage.removeItem(key);
      }
    });

    // ✅ STEP 2: Clear all sessionStorage keys
    const sessionStorageKeys = [
      // Authentication tokens
      "access_token",
      "refresh_token",
      "token_type",
      
      // User identification
      "role",
      "username",
      "email",
      "user_id",
      "userId",
      "name",
      "surname",
      "student_id",
      "teacher_id",
      
      // Session tracking
      "lastUserId",  // ✅ Added - used by TutorAgent
      "currentSessionId",
      "conversationHistory",
      
      // Temporary data
      "tempFormData",
      "redirectAfterLogin"
    ];

    sessionStorageKeys.forEach(key => {
      if (sessionStorage.getItem(key)) {
        console.log(`  🗑️ Removing sessionStorage: ${key}`);
        sessionStorage.removeItem(key);
      }
    });

    // ✅ STEP 3: Clear ALL localStorage (nuclear option - use with caution)
    // Uncomment if you want to clear everything
    // console.log("  🗑️ Clearing all localStorage");
    // localStorage.clear();
    
    // ✅ STEP 4: Clear ALL sessionStorage
    console.log("  🗑️ Clearing all sessionStorage");
    sessionStorage.clear();

    // ✅ STEP 5: Clear any cookies (if you're using them)
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // ✅ STEP 6: Close sidebar
    setIsSidebarOpen(false);

    // ✅ STEP 7: Reset any React state that might hold user data
    // (If you're using Context API or Redux, clear those too)

    // ✅ STEP 8: Navigate to login page
    console.log("  ✅ Logout complete, redirecting to login");
    navigate("/login", { replace: true });

    // ✅ STEP 9: Optional - Force page reload to clear any cached data
    // Uncomment if you want a full page refresh after logout
    // setTimeout(() => {
    //   window.location.href = "/login";
    // }, 100);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Navigation items based on auth state
  const getNavigationItems = () => {
    if (!isAuthenticated) {
      return [
        { path: "/about", label: "About", icon: Info },
        { path: "/contact", label: "Contact", icon: Mail },
      ];
    }

    const authenticatedItems = [
      { path: "/home", label: "Home", icon: Home },
      { path: "/courses", label: "Courses", icon: BookOpen },
    ];

    if (userRole === "student") {
      authenticatedItems.push(
        { path: "/student-dashboard", label: "Dashboard", icon: GraduationCap }
      );
    } else if (userRole === "teacher") {
      authenticatedItems.push(
        { path: "/teacher-dashboard", label: "Dashboard", icon: Users }
      );
    } else if (userRole === "admin") {
      authenticatedItems.push(
        { path: "/admin-dashboard", label: "Admin", icon: Users }
      );
    }

    return authenticatedItems;
  };

  const getAccountItems = () => {
    const items = [];

    if (userRole === "student") {
      items.push(
        { path: "/my-progress", label: "My Progress", icon: TrendingUp },
        { path: "/my-courses", label: "My Courses", icon: BookOpen },
        { path: "/subscribe", label: "Subscription", icon: CreditCard }
      );
    } else if (userRole === "teacher") {
      items.push(
        { path: "/my-classes", label: "My Classes", icon: Users },
        { path: "/lesson-plans", label: "Lesson Plans", icon: FileText }
      );
    }

    return items;
  };

  const navigationItems = getNavigationItems();
  const accountItems = getAccountItems();

  return (
    <>
      <header className="header">
        <div className="header-content">
          {/* Logo */}
          <Link 
            to={isAuthenticated ? "/home" : "/"} 
            className="logo"
          >
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? "active" : ""}`}
              >
                {item.label}
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="account-dropdown">
                <button className="account-button">
                  <User size={18} />
                  <span>Account</span>
                </button>
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <User size={16} />
                    <span>{userName}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  {accountItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-item"
                    >
                      <item.icon size={16} />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                  <div className="dropdown-divider"></div>
                  <button onClick={handleLogout} className="dropdown-item logout">
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="auth-buttons">
                <Link to="/login" className="btn-login">
                  Login
                </Link>
                <Link to="/register" className="btn-register">
                  Register
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Hamburger Menu */}
          <button 
            className="mobile-menu-button" 
            onClick={toggleSidebar}
            aria-label="Toggle menu"
          >
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div className="mobile-sidebar-overlay" onClick={closeSidebar}></div>
      )}

      {/* Mobile Sidebar */}
      <aside className={`mobile-sidebar ${isSidebarOpen ? "open" : ""}`}>
        <div className="sidebar-content">
          <div className="sidebar-topbar">
            <span className="sidebar-title">Menu</span>
            <button
              type="button"
              className="sidebar-close-button"
              onClick={closeSidebar}
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          {/* User Info (if authenticated) */}
          {isAuthenticated && (
            <div className="sidebar-user">
              <div className="user-avatar">
                <User size={24} />
              </div>
              <div className="user-info">
                <p className="user-name">{userName}</p>
                <p className="user-role">{userRole}</p>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="sidebar-section">
            <h3 className="sidebar-section-title">Menu</h3>
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${location.pathname === item.path ? "active" : ""}`}
                onClick={closeSidebar}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Account Links (if authenticated) */}
          {isAuthenticated && (
            <div className="sidebar-section">
              <h3 className="sidebar-section-title">Account</h3>
              {accountItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${location.pathname === item.path ? "active" : ""}`}
                  onClick={closeSidebar}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          )}

          {/* Auth Buttons or Logout */}
          <div className="mobile-sidebar-footer">
            {isAuthenticated ? (
              <button onClick={handleLogout} className="sidebar-logout">
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            ) : (
              <div className="sidebar-auth-buttons">
                <Link to="/login" className="btn-login-mobile">
                  Login
                </Link>
                <Link to="/register" className="btn-register-mobile">
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Header;